// File: src/lib/thumbs/useThumbnails.ts
import * as React from "react";

export type ThumbFrame = {
  timeSec: number;
  blob: Blob | null;
  url: string | null; // object URL for <img>
};

const FFMPEG_ENABLED = import.meta.env.VITE_ENABLE_FFMPEG === "true";

type Provider = "auto" | "video" | "ffmpeg";

export type UseThumbnailsOptions = {
  provider?: Provider;
  height?: number; // css px; used by video extractor
  frameCount?: number;
  times?: unknown; // optional external sampling, sanitized
  maxThumbs?: number;
  strideSec?: number;
  width?: number; // used by ffmpeg worker (legacy)
};

export type UseThumbnailsResult = {
  frames: ThumbFrame[];
  urls: string[]; // convenience mirror of frames[].url filtered
  loading: boolean;
  error: string | null;
};

/* ---------------------------------------------------------
 * Small helpers
 * --------------------------------------------------------- */

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function finiteNum(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

function readStr(v: unknown): string {
  return typeof v === "string" && v.trim() ? v.trim() : "";
}

function uniqSortedFinite(nums: number[]): number[] {
  const out = nums.filter((n) => finiteNum(n) && n >= 0).slice();
  out.sort((a, b) => a - b);

  const dedup: number[] = [];
  for (const n of out) {
    if (!dedup.length || Math.abs(dedup[dedup.length - 1] - n) > 1e-6) dedup.push(n);
  }
  return dedup;
}

function normalizeTimes(
  input: unknown,
  durationSec: number,
  maxThumbs: number,
  strideSec?: number,
  requestedCount?: number,
): number[] {
  // If caller provided an array, use it (sanitized).
  if (Array.isArray(input)) {
    const nums = input.map((x) => (typeof x === "string" ? Number(x) : (x as number)));
    const clean = uniqSortedFinite(nums).map((t) => clamp(t, 0, Math.max(0, durationSec)));
    return clean;
  }

  if (!finiteNum(durationSec) || durationSec <= 0) return [];
  const dur = Math.max(0, durationSec);

  // Fixed stride mode.
  if (finiteNum(strideSec) && strideSec > 0) {
    const step = Math.max(0.05, strideSec);
    const count = clamp(Math.floor(dur / step) + 1, 1, maxThumbs);
    const times: number[] = [];
    for (let i = 0; i < count; i++) times.push(clamp(i * step, 0, dur));
    return uniqSortedFinite(times);
  }

  // Evenly spaced sample set.
  const count = finiteNum(requestedCount) ? clamp(requestedCount, 1, maxThumbs) : clamp(12, 1, maxThumbs);
  if (count === 1) return [0];
  const times = Array.from({ length: count }, (_, i) => (i / (count - 1)) * dur);
  return uniqSortedFinite(times);
}

function framesSame(a: ThumbFrame[], b: ThumbFrame[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i]?.timeSec !== b[i]?.timeSec) return false;
    if (a[i]?.url !== b[i]?.url) return false;
  }
  return true;
}

/* ---------------------------------------------------------
 * FFMPEG: single-frame helper (legacy)
 * --------------------------------------------------------- */

export async function getThumbAtTime(file: Blob | File, timeSec: number, width = 320): Promise<Blob | null> {
  if (!FFMPEG_ENABLED) return null;

  const ThumbWorker = (await import("@/workers/ffmpeg-thumb.worker?worker")).default as new () => Worker;
  const worker = new ThumbWorker();

  const buf = await file.arrayBuffer();

  return await new Promise<Blob | null>((resolve) => {
    const onMessage = (ev: MessageEvent<any>) => {
      const d = ev.data;
      if (d?.type === "thumb:done" && d.blob) {
        worker.removeEventListener("message", onMessage);
        worker.terminate();
        resolve(new Blob([d.blob], { type: "image/jpeg" }));
      } else if (d?.error) {
        // eslint-disable-next-line no-console
        console.error("[useThumbnails] worker error:", d.error);
        worker.removeEventListener("message", onMessage);
        worker.terminate();
        resolve(null);
      }
    };
    worker.addEventListener("message", onMessage);
    worker.postMessage({ type: "ping" });
    worker.postMessage({ type: "thumb", src: buf, timeSec, width });
  });
}

/* ---------------------------------------------------------
 * VIDEO extractor (src URL -> frames)
 * --------------------------------------------------------- */

async function extractVideoFrames(args: {
  src: string;
  durationSec: number;
  times: number[];
  heightPx: number;
  signal?: AbortSignal;
}): Promise<ThumbFrame[]> {
  const { src, durationSec, times, heightPx, signal } = args;

  const s = readStr(src);
  if (!s) return [];
  if (!finiteNum(durationSec) || durationSec <= 0) return [];
  if (!Array.isArray(times) || times.length === 0) return [];

  const video = document.createElement("video");
  video.preload = "auto";
  video.muted = true;
  video.playsInline = true;
  video.crossOrigin = "anonymous";
  video.src = s;

  const waitEvent = (type: string) =>
    new Promise<void>((resolve, reject) => {
      const onOk = () => {
        cleanup();
        resolve();
      };
      const onErr = () => {
        cleanup();
        reject(new Error(`video:${type}:error`));
      };
      const onAbort = () => {
        cleanup();
        reject(new DOMException("Aborted", "AbortError"));
      };
      const cleanup = () => {
        video.removeEventListener(type, onOk);
        video.removeEventListener("error", onErr);
        signal?.removeEventListener("abort", onAbort);
      };
      video.addEventListener(type, onOk, { once: true });
      video.addEventListener("error", onErr, { once: true });
      signal?.addEventListener("abort", onAbort, { once: true });
    });

  await waitEvent("loadedmetadata");

  const vw = finiteNum(video.videoWidth) ? video.videoWidth : 0;
  const vh = finiteNum(video.videoHeight) ? video.videoHeight : 0;
  if (vw <= 0 || vh <= 0) return [];

  // ✅ Patch: DPR-aware 2× capture
  // - keep CSS height unchanged (caller still renders at opts.height)
  // - capture at (heightPx * dpr * 2) so browser downsamples crisply
  const dpr =
    typeof window !== "undefined" && Number.isFinite(window.devicePixelRatio) ? Math.max(1, window.devicePixelRatio) : 1;

  // "2× beyond dpr" as requested; clamp so we don't explode on insane DPRs.
  // Example:
  // - dpr=1 => scale=2
  // - dpr=2 => scale=4
  // - dpr=3 => scale=6 (clamped)
  const scale = clamp(dpr * 2, 2, 6);

  const outH = clamp(Math.floor(heightPx * scale), 16, 1024);
  const outW = clamp(Math.floor((vw / vh) * outH), 16, 4096);

  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;

  const ctx = canvas.getContext("2d");
  if (!ctx) return [];

  const seekTo = async (t: number) => {
    const tt = clamp(t, 0, Math.max(0, durationSec));
    if (Math.abs(video.currentTime - tt) < 1e-3) return;
    video.currentTime = tt;
    await waitEvent("seeked");
  };

  const out: ThumbFrame[] = [];
  for (const t of times) {
    if (signal?.aborted) break;

    try {
      await seekTo(t);
      ctx.clearRect(0, 0, outW, outH);

      // ✅ Keep drawImage exactly as-is
      ctx.drawImage(video, 0, 0, outW, outH);

      const blob: Blob = await new Promise((resolve) =>
        canvas.toBlob((b) => resolve(b ?? new Blob()), "image/jpeg", 0.72),
      );

      const url = URL.createObjectURL(blob);
      out.push({ timeSec: t, blob, url });
    } catch {
      // skip this timestamp
    }
  }

  // Best-effort cleanup
  try {
    video.removeAttribute("src");
    video.load();
  } catch {
    // ignore
  }

  return out;
}

/* ---------------------------------------------------------
 * Overloads
 * --------------------------------------------------------- */

// Legacy: file + explicit times
export function useThumbnails(
  file: Blob | File | null,
  times: number[],
  width?: number,
): { frames: ThumbFrame[]; loading: boolean; error: string | null };

// New: src URL + duration + options (ThumbnailStrip)
export function useThumbnails(src: string, durationSec: number, opts?: UseThumbnailsOptions): UseThumbnailsResult;

// Implementation
export function useThumbnails(a: any, b: any, c?: any): any {
  // ✅ Deterministic overload routing (fixes legacy misclassification)
  const isLegacyFileCall = (a instanceof Blob || a instanceof File) && Array.isArray(b);
  const isSrcCall = typeof a === "string" && typeof b === "number";

  // Normalize into a single internal shape:
  const src: string = isSrcCall ? readStr(a) : "";
  const durationSec: number = isSrcCall ? (finiteNum(b) ? b : 0) : 0;

  const legacyFile: Blob | File | null = isLegacyFileCall ? (a as Blob | File | null) : null;
  const legacyTimes: number[] = isLegacyFileCall && Array.isArray(b) ? (b as number[]) : [];
  const legacyWidth = isLegacyFileCall ? (finiteNum(c) ? c : 160) : undefined;

  const opts: UseThumbnailsOptions | undefined = isSrcCall ? (c as UseThumbnailsOptions | undefined) : undefined;

  const provider: Provider = opts?.provider ?? "auto";
  const heightPx = finiteNum(opts?.height) ? opts!.height! : 72;
  const frameCount = finiteNum(opts?.frameCount) ? opts!.frameCount! : 12;
  const maxThumbs = finiteNum(opts?.maxThumbs) ? clamp(opts!.maxThumbs!, 1, 64) : 64;
  const strideSec = opts?.strideSec;

  // ✅ stable times for src-call mode
  const srcTimes = React.useMemo(() => {
    return normalizeTimes(opts?.times, durationSec, maxThumbs, strideSec, frameCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    src,
    durationSec,
    maxThumbs,
    strideSec,
    frameCount,
    Array.isArray(opts?.times) ? (opts!.times as any[]).join(",") : String(opts?.times ?? ""),
  ]);

  const srcTimesKey = React.useMemo(() => srcTimes.join(","), [srcTimes]);

  const [frames, setFrames] = React.useState<ThumbFrame[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Track object URLs to revoke
  const urlsRef = React.useRef<string[]>([]);
  React.useEffect(() => {
    return () => {
      for (const u of urlsRef.current) URL.revokeObjectURL(u);
      urlsRef.current = [];
    };
  }, []);

  React.useEffect(() => {
    let alive = true;
    const ctrl = new AbortController();

    async function run() {
      try {
        setError(null);

        // -------------------------
        // Legacy path: file + times
        // -------------------------
        if (isLegacyFileCall) {
          const file = legacyFile;
          const timesRaw = Array.isArray(legacyTimes) ? legacyTimes : [];
          const cleanTimes = uniqSortedFinite(timesRaw).map((t) => clamp(t, 0, Number.MAX_SAFE_INTEGER));

          if (!file || cleanTimes.length === 0) {
            if (!alive) return;
            setFrames((prev) => (prev.length ? [] : prev));
            setLoading(false);
            return;
          }

          setLoading(true);

          const width = finiteNum(legacyWidth) ? legacyWidth : 160;

          // Revoke prior urls before regenerating
          for (const u of urlsRef.current) URL.revokeObjectURL(u);
          urlsRef.current = [];

          const next: ThumbFrame[] = [];
          for (const t of cleanTimes) {
            if (!alive || ctrl.signal.aborted) break;
            const blob = await getThumbAtTime(file, t, width);
            const url = blob ? URL.createObjectURL(blob) : null;
            if (url) urlsRef.current.push(url);
            next.push({ timeSec: t, blob, url });
          }

          if (!alive) return;

          setFrames((prev) => (framesSame(prev, next) ? prev : next));
          setLoading(false);
          return;
        }

        // -------------------------
        // Src path: src + duration
        // -------------------------
        if (!isSrcCall) {
          if (!alive) return;
          setFrames((prev) => (prev.length ? [] : prev));
          setLoading(false);
          return;
        }

        const s = readStr(src);
        if (!s || !finiteNum(durationSec) || durationSec <= 0 || !srcTimes.length) {
          if (!alive) return;
          setFrames((prev) => (prev.length ? [] : prev));
          setLoading(false);
          return;
        }

        setLoading(true);

        // Only video extractor available for src strings.
        // If provider=ffmpeg here, we just return empty (safe).
        const canUseVideo = provider === "auto" || provider === "video";
        const next = canUseVideo
          ? await extractVideoFrames({
              src: s,
              durationSec,
              times: srcTimes,
              heightPx,
              signal: ctrl.signal,
            })
          : [];

        if (!alive) return;

        // Revoke previous urls, then adopt new ones
        for (const u of urlsRef.current) URL.revokeObjectURL(u);
        urlsRef.current = next.map((f) => f.url).filter(Boolean) as string[];

        setFrames((prev) => (framesSame(prev, next) ? prev : next));
        setLoading(false);
      } catch (e: any) {
        if (!alive) return;
        setLoading(false);
        setError(String(e?.message || e || "Thumbnail generation failed"));
      }
    }

    run();

    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [
    isLegacyFileCall,
    isSrcCall,
    src,
    durationSec,
    provider,
    heightPx,
    srcTimesKey,
    legacyFile,
    legacyWidth,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    Array.isArray(legacyTimes) ? legacyTimes.join(",") : String(legacyTimes ?? ""),
  ]);

  const urls = React.useMemo(() => frames.map((f) => f.url).filter(Boolean) as string[], [frames]);

  if (isLegacyFileCall) {
    return { frames, loading, error };
  }

  return { frames, urls, loading, error };
}

export default { useThumbnails };
